import { InsurerStatus } from '@/@types/generated'
import { Breadcrumb } from '@/components/Atoms/Breadcrumbs'
import { InsurerStatusVariants } from '@/lib/format/InsurerStatus'
import Loading from '@/components/Atoms/Loading'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { Header } from '@/components/ui/header'
import { Separator } from '@/components/ui/separator'
import format from '@/lib/format'
import { buildPath, Params } from '@/routes'
import { Building2Icon, PhoneIcon } from 'lucide-react'
import { Suspense, useCallback } from 'react'
import { Navigate, Outlet, useNavigate, useParams } from 'react-router'
import { InsurerProvider, useInsurer } from '../hooks/insurer-provider'
import { AuthFlag } from './AuthFlag'
import { InfoField } from './InfoField'
import InsurerNavigation from './InsurerNavigation'

export function InsurerLayout() {
  const { licenseNumber } =
    useParams<Params<'/insurance/insurers/:licenseNumber'>>()
  const navigate = useNavigate()

  const handleError = useCallback(() => {
    navigate(buildPath('/insurance/insurers'))
  }, [navigate])

  if (!licenseNumber || !Number.isInteger(Number(licenseNumber)))
    return <Navigate to={buildPath('/insurance/insurers')} replace />

  return (
    <InsurerProvider
      licenseNumber={Number(licenseNumber)}
      onError={handleError}
    >
      <InsurerLayoutInner />
    </InsurerProvider>
  )
}

function InsurerLayoutInner() {
  const { insurer } = useInsurer()

  const displayPhone =
    insurer.officeArea && insurer.officePhone
      ? format.PhoneNumber(`${insurer.officeArea}${insurer.officePhone}`)
      : null

  const tollFreePhone =
    insurer.tollFreeComplaintArea && insurer.tollFreeComplaintPhone
      ? format.PhoneNumber(
          `${insurer.tollFreeComplaintArea}${insurer.tollFreeComplaintPhone}`
        )
      : null

  const statusLabel = format.Enum(InsurerStatus, insurer.status)
  const statusVariant = insurer.status
    ? InsurerStatusVariants[insurer.status]
    : 'secondary'

  return (
    <>
      <Breadcrumb
        label={insurer.companyName ?? `TDI #${insurer.licenseNumber}`}
        path={buildPath('/insurance/insurers/:licenseNumber', {
          licenseNumber: String(insurer.licenseNumber),
        })}
      />

      <div className="flex flex-col justify-between gap-4 md:flex-row">
        <div className="flex min-w-0 flex-col gap-1">
          <Header
            variant="h1"
            className="overflow-hidden overflow-ellipsis whitespace-nowrap"
          >
            {insurer.companyName ?? `TDI #${insurer.licenseNumber}`}
          </Header>
        </div>
      </div>

      <Card>
        <CardHeader>
          <h3 className="flex items-center gap-2 text-2xl font-semibold">
            <Building2Icon className="size-5 flex-none" />
            Insurer Information
            <Badge variant={statusVariant}>{statusLabel}</Badge>
          </h3>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-x-8 gap-y-2 sm:grid-cols-2 lg:grid-cols-3">
          <InfoField label="TDI License Number" valueClassName="font-mono">
            {insurer.licenseNumber}
          </InfoField>

          <InfoField label="Company Name">
            {insurer.companyName ?? '—'}
          </InfoField>

          <InfoField label="Domicile">
            {insurer.domicileCity && insurer.domicileState
              ? `${insurer.domicileCity}, ${insurer.domicileState}`
              : '—'}
          </InfoField>

          <InfoField label="TDI Company ID" valueClassName="font-mono">
            {insurer.companyId ?? '—'}
          </InfoField>

          <InfoField label="Status Since">
            {format.Date(insurer.statusDateChange)}
          </InfoField>

          <Separator className="my-2 sm:col-span-2 lg:col-span-3" />

          <InfoField label="Address">
            {insurer.address ? format.Address(insurer.address) : '—'}
          </InfoField>

          {insurer.poBoxAddressLine && (
            <InfoField label="PO Box">{insurer.poBoxAddressLine}</InfoField>
          )}

          <Separator className="my-2 sm:col-span-2 lg:col-span-3" />

          <InfoField
            label="Office Phone"
            valueClassName="flex items-center gap-1"
          >
            {displayPhone ? (
              <>
                <PhoneIcon className="size-4 flex-none" />
                {displayPhone}
              </>
            ) : (
              '—'
            )}
          </InfoField>

          {tollFreePhone && (
            <InfoField
              label="Toll-Free Complaint Line"
              valueClassName="flex items-center gap-1"
            >
              <PhoneIcon className="size-4 flex-none" />
              {tollFreePhone}
            </InfoField>
          )}

          <Separator className="my-2 sm:col-span-2 lg:col-span-3" />

          <InfoField label="Main Contact">
            {insurer.firstName || insurer.lastName
              ? `${insurer.firstName ?? ''} ${insurer.lastName ?? ''}`.trim()
              : '—'}
          </InfoField>

          <InfoField label="Contact Email" valueClassName="break-all">
            {insurer.emailAddress ?? '—'}
          </InfoField>

          <Separator className="my-2 sm:col-span-2 lg:col-span-3" />

          <div className="sm:col-span-2 lg:col-span-3">
            <InfoField label="Authorized Lines of Insurance">
              <div className="flex flex-wrap gap-6">
                <AuthFlag
                  value={insurer.authToSellAutoLiabil}
                  label="Auto Liability"
                />
                <AuthFlag
                  value={insurer.authToSellInlandMarine}
                  label="Inland Marine"
                />
                <AuthFlag
                  value={insurer.authToSellFidelitySurety}
                  label="Fidelity / Surety"
                />
              </div>
            </InfoField>
          </div>

          <Separator className="my-2 sm:col-span-2 lg:col-span-3" />

          <InfoField label="Company Classification" valueClassName="font-mono">
            {insurer.surplusLinesRiskRetenCo || '—'}
          </InfoField>

          {insurer.contactForNonUsSlCo && (
            <InfoField
              label="Contact for Non-US Surplus Lines Co."
              className="sm:col-span-2"
            >
              {insurer.contactForNonUsSlCo}
            </InfoField>
          )}
        </CardContent>
      </Card>

      <InsurerNavigation licenseNumber={insurer.licenseNumber} />

      <Suspense fallback={<Loading variant="fullscreen" />}>
        <Outlet />
      </Suspense>
    </>
  )
}
